import { useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface CIScorePillProps {
  score: number;
  trend?: 'up' | 'down' | 'stable';
  sparklineData?: number[]; // 30-day trend data
  className?: string;
}

export function CIScorePill({ score, trend = 'stable', sparklineData, className = '' }: CIScorePillProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Determine tier and color based on score
  const getTier = () => {
    if (score >= 75) return 'A';
    if (score >= 50) return 'B';
    return 'C';
  };

  const getColorClasses = () => {
    const tier = getTier();
    switch (tier) {
      case 'A':
        return {
          ring: 'ring-2 ring-green-500/60',
          bg: 'bg-green-500/15',
          text: 'text-green-500',
          gradient: 'from-green-500/30 to-green-600/30'
        };
      case 'B':
        return {
          ring: 'ring-2 ring-yellow-500/60',
          bg: 'bg-yellow-500/15',
          text: 'text-yellow-500',
          gradient: 'from-yellow-500/30 to-yellow-600/30'
        };
      case 'C':
        return {
          ring: 'ring-2 ring-orange-500/60',
          bg: 'bg-orange-500/15',
          text: 'text-orange-500',
          gradient: 'from-orange-500/30 to-orange-600/30'
        };
    }
  };

  const colors = getColorClasses();
  const tier = getTier();

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-3 h-3" />;
      case 'down':
        return <TrendingDown className="w-3 h-3" />;
      default:
        return <Minus className="w-3 h-3" />;
    }
  };

  // Render mini sparkline
  const renderSparkline = () => {
    if (!sparklineData || sparklineData.length === 0) return null;

    const max = Math.max(...sparklineData);
    const min = Math.min(...sparklineData);
    const range = max - min || 1;

    return (
      <div className="flex items-end gap-[2px] h-8">
        {sparklineData.map((value, i) => {
          const height = ((value - min) / range) * 100;
          return (
            <div
              key={i}
              className={`w-1 bg-gradient-to-t ${colors.gradient} rounded-t-sm transition-all`}
              style={{ height: `${Math.max(height, 10)}%` }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colors.ring} ${colors.bg} transition-all duration-300 hover:scale-105 cursor-pointer ${className}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <span className={`text-sm font-bold ${colors.text}`}>
              {score}
            </span>
            <span className={`text-xs font-medium ${colors.text}`}>
              Tier {tier}
            </span>
            <div className={colors.text}>
              {getTrendIcon()}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          className="glass-card border-2 p-4 min-w-[240px]"
          sideOffset={8}
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">CI Score</span>
              <span className={`text-2xl font-bold ${colors.text}`}>{score}</span>
            </div>
            
            {sparklineData && sparklineData.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-2">30-day trend</p>
                {renderSparkline()}
              </div>
            )}

            <div className="pt-2 border-t border-border/50">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Relationship Strength</span>
                <span className={colors.text}>
                  {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trend}
                </span>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              {tier === 'A' && 'Strong relationship, high engagement'}
              {tier === 'B' && 'Moderate relationship, room to improve'}
              {tier === 'C' && 'Weak relationship, needs attention'}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
